import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'next-i18next';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';

import { useAddNewContract } from '@/modules/workforce/Contracts/apiHooks';
import EditContractForm from '@/modules/workforce/Contracts/components/CreateAndEdit/Edit';
import { useCreateEditContractUtils } from '@/modules/workforce/Contracts/components/CreateAndEdit/logic';
import { IContracts } from '@/modules/workforce/Contracts/types';
import { useCreateTechnicalOperationUtils } from '@/modules/workingDay/components/CreateTechnicalOperation/logic';
import ConfirmationModal from '@/shared/components/molecules/ConfirmationModal';
import MultiStepForm from '@/shared/components/molecules/MultiStepForm';
import { useStore } from '@/shared/hooks/context/createGenericContext';
import { useModal } from '@/shared/hooks/context/modalContext/useModal';
import { IGeneral } from '@/shared/types';

const CreateEditContract: React.FC<{
  supplier?: IGeneral.NameIdPair;
  supplierAssignmentId?: number;
}> = ({ supplier, supplierAssignmentId }) => {
  const { t } = useTranslation();

  const { openModal } = useModal();

  const [selectedAsset] = useStore((store) => store?.asset);

  const {
    getFormSteps,
    addContractValidationSchema,
    editContractValidationSchema,
    getAddMutation,
  } = useCreateEditContractUtils();

  const { mutate: add, isLoading: isAddingContract } = useAddNewContract();

  const methods = useForm<IContracts.IAddContractSchema>({
    resolver: yupResolver(
      supplierAssignmentId
        ? editContractValidationSchema
        : addContractValidationSchema
    ),
    mode: 'all',
    reValidateMode: 'onChange',
    defaultValues: {
      entity: {
        name: selectedAsset?.name,
        id: selectedAsset?.id,
        currency: selectedAsset?.currency,
      },
      supplier,
      startDate: new Date(),
      endDate: new Date(new Date().setDate(new Date().getDate() + 14)),
      unitBased: [
        {
          unitEditable: true,
        },
      ],
    },
  });

  const dailyWageWatcher = methods.watch('dailyWage');

  const { openCreateTechnicalOperationModal } =
    useCreateTechnicalOperationUtils();

  const onSuccess = () =>
    openModal(
      <ConfirmationModal
        header={t('create-contract-success')}
        variant='success'
        message={t('start-creating-operations')}
        onDismissRemoveNumber={2}
        onConfirmRemoveNumber={2}
        confirmText={t('create-operation')}
        onConfirm={openCreateTechnicalOperationModal}
        buttonsAlignment='col'
      />,
      {
        dismissable: false,
      }
    );

  const onSubmit: SubmitHandler<IContracts.IAddContractSchema> = (values) => {
    add(
      { ...getAddMutation(values) },
      {
        onSuccess,
      }
    );
  };

  return supplierAssignmentId ? (
    <FormProvider {...methods}>
      <EditContractForm supplierAssignmentId={supplierAssignmentId} />
    </FormProvider>
  ) : (
    <MultiStepForm<IContracts.IAddContractSchema>
      steps={getFormSteps(dailyWageWatcher)}
      formMethods={methods}
      onSubmit={onSubmit}
      isMutating={isAddingContract}
      submitButtonLabel={t('create-contract')}
    />
  );
};

export { CreateEditContract };
